// src/shared/ui.js — общи парчета интерфейс (копие от app.js, Фаза 3).

import { el, input } from './dom.js';
import { dfmt } from './format.js';

// Грешка при ЗАРЕЖДАНЕ не бива да изглежда като „няма данни" (Фаза А.4):
// сривът показваше празното състояние („Още няма задачи.") и човек решаваше,
// че записите му са изтрити. Панелът казва истината и дава „Опитай пак",
// който вика същото зареждане. role="alert" — четецът го обявява; отделен
// toast НЕ се пуска, панелът Е съобщението.
export function loadError(retry, message) {
  return el('div', { class: 'load-error panel', role: 'alert' },
    el('div', { class: 'load-error-ic', 'aria-hidden': 'true', text: '⚠' }),
    el('p', { text: message || 'Данните не можаха да се заредят. Проверете връзката.' }),
    el('button', { class: 'btn', text: 'Опитай пак', onClick: retry }));
}

// ЕДИННИТЕ подраздели на приложението. Бяха направени по три начина и в
// Чек лист, Работна бланка и Такси избраният почти не личеше: там бутоните
// носеха „btn sm"/„btn sm ghost", а правилото .vtabs button махаше фона им.
// Екранният четец пък не знаеше кой е избран. Оттук излизат всички:
// role="tablist"/"tab", aria-selected и видимо подчертаване (.active).
// Пази го test/tabs-unified.test.js — подраздели на ръка не минават.
//   tabs     — [{ id, label, disabled?, title? }]
//   active   — id на избрания
//   onSelect — (id) => …; не се вика за забранения. За избрания СЕ вика:
//              в Склад повторното натискане връща към началото на раздела.
//   label    — име на лентата за екранния четец („Раздели на склада")
//   opts.inline — в лентата с филтри: без собствена линия и отстъп
export function tabBar(tabs, active, onSelect, label, opts) {
  const o = opts || {};
  return el('div', { class: 'vtabs' + (o.inline ? ' inline' : ''), role: 'tablist', 'aria-label': label },
    ...tabs.map((t) => {
      const on = t.id === active;
      return el('button', {
        type: 'button', role: 'tab',
        class: (on ? 'active' : '') + (t.disabled ? ' disabled' : ''),
        'aria-selected': on ? 'true' : 'false',
        'aria-disabled': t.disabled ? 'true' : null,
        title: t.title || null,
        text: t.label,
        onClick: () => { if (!t.disabled) onSelect(t.id); },
      });
    }));
}

// Периодът във филтрите: „Период: от – до" като ЕДНА група, за да личи, че
// двете дати вървят заедно (Макс, 19.10.2026). Полетата идват от dateInput().
export function periodGroup(fromI, toI) {
  fromI.setAttribute('aria-label', fromI.getAttribute('aria-label') || 'От дата (период)');
  toI.setAttribute('aria-label', toI.getAttribute('aria-label') || 'До дата (период)');
  return el('div', { class: 'filter-period', role: 'group', 'aria-label': 'Период' },
    el('span', { class: 'filter-period-label', 'aria-hidden': 'true', text: 'Период' }), fromI,
    el('span', { class: 'filter-period-sep', 'aria-hidden': 'true', text: '–' }), toI);
}

// ЕДИННАТА търсачка на приложението (ползва се от всички екрани).
// Лупа в полето, бутон „×" при въведен текст, изчакване (debounce) преди
// търсенето, за да не се пречертава списъкът на всеки натиснат клавиш.
// onSearch получава текста в малки букви и без празни отстрани.
export function searchInput({ placeholder = 'Търсене…', onSearch, ariaLabel, delay = 200, value = '' } = {}) {
  const inp = input({ class: 'searchbox-input', type: 'search', placeholder, value, 'aria-label': ariaLabel || placeholder });
  const clearBtn = el('button', { class: 'searchbox-clear', type: 'button', 'aria-label': 'Изчисти търсенето', text: '×' });
  const box = el('div', { class: 'searchbox' },
    el('span', { class: 'searchbox-ic', 'aria-hidden': 'true', html: '<svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="8.5" cy="8.5" r="5.5"/><path d="m13 13 4 4"/></svg>' }),
    inp, clearBtn
  );

  let timer = null;
  const fire = () => { if (onSearch) onSearch(inp.value.trim().toLowerCase()); };
  const sync = () => box.classList.toggle('has-value', inp.value !== '');
  sync(); // показва бутона „×", ако има начална стойност

  inp.addEventListener('input', () => {
    sync();
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => { timer = null; fire(); }, delay);
  });
  // Enter търси веднага (без да чака изчакването).
  inp.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { if (timer) { clearTimeout(timer); timer = null; } fire(); }
    else if (e.key === 'Escape' && inp.value) { e.stopPropagation(); doClear(); }
  });
  function doClear() {
    if (timer) { clearTimeout(timer); timer = null; }
    inp.value = ''; sync(); fire(); inp.focus();
  }
  // Без stopPropagation — кликът трябва да стигне до document (там рамката
  // затваря менютата за фирма/профил).
  clearBtn.addEventListener('click', (e) => { e.preventDefault(); doClear(); });

  box.inputEl = inp;   // за екрани, които искат да фокусират полето
  box.clearEl = clearBtn;
  return box;
}

// Заглавие на панел. Когато съвпада ДОСЛОВНО с името на екрана в горната
// лента („Фактури" над „Фактури"), визуално се скрива — за екранния четец
// h2 остава (структурата на страницата не губи ниво), а търсачката пак
// получава името си от title. Табовете („ВОП по чл. 117") не съвпадат и
// заглавието им си стои видимо.
export function panelH2(title) {
  const topbar = document.querySelector('.topbar h1');
  const dup = !!topbar && topbar.textContent.trim() === String(title).trim();
  const h2 = el('h2', { class: dup ? 'sr-only' : '', text: title });
  // sr-only маха h2 от потока → flex:1 се губи; отделен разпъвач пази
  // бутоните вдясно.
  return dup ? [h2, el('div', { style: 'flex:1', 'aria-hidden': 'true' })] : [h2];
}

export function panelHead(title, addLabel, onAdd, onSearch) {
  const head = el('div', { class: 'panel-head' }, ...panelH2(title));
  if (onSearch) head.appendChild(searchInput({ placeholder: 'Търсене…', onSearch, ariaLabel: 'Търсене в „' + title + '"' }));
  if (onAdd) head.appendChild(el('button', { class: 'btn sm', text: '+ ' + addLabel, onClick: onAdd }));
  return head;
}

export function emptyState(icon, text, btnLabel, onClick) {
  return el('div', { class: 'empty' },
    el('div', { class: 'big', text: icon }),
    el('p', { text: text }),
    onClick ? el('button', { class: 'btn', text: btnLabel, onClick }) : null
  );
}

// Падащо меню с рядко ползвани действия („Действия ▾").
// items: [{ label, onClick }] — null/false елементи се пропускат.
// Достъпност: aria-haspopup/expanded, role=menu/menuitem, Esc затваря и
// връща фокуса на бутона, ArrowDown отваря и фокусира първото, Tab обхожда
// (излезе ли фокусът от менюто, то се затваря), клик извън също затваря.
export function actionsMenu(label, items) {
  const list = (items || []).filter(Boolean);
  const wrap = el('div', { class: 'actions-menu' });
  const btn = el('button', {
    class: 'btn sm ghost', type: 'button', 'aria-haspopup': 'true', 'aria-expanded': 'false',
    text: label + ' ▾',
  });
  let menu = null;

  function close(focusBtn) {
    if (!menu) return;
    menu.remove(); menu = null;
    btn.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', onDocClick);
    window.removeEventListener('resize', onViewportChange);
    window.removeEventListener('scroll', onViewportChange, true);
    if (focusBtn) btn.focus();
  }
  // „Вътре в менюто" = в обвивката (бутона) ИЛИ в самото меню, което живее
  // отделно в <body> (виж open()).
  const inside = (n) => !!n && (wrap.contains(n) || (menu && menu.contains(n)));
  function onDocClick(e) { if (!inside(e.target)) close(false); }
  function onViewportChange() { close(false); }

  // Менюто се закача в <body>, а НЕ до бутона, по две причини:
  //  1) .panel е overflow:hidden и би отрязал менюто при кратък списък;
  //  2) .page има анимация с transform, а трансформиран предшественик става
  //     отправна точка за position:fixed — координатите щяха да са спрямо
  //     него, не спрямо екрана (менюто излизаше вдясно от прозореца).
  // Затова: fixed + ръчно сметнати екранни координати; при скрол/преоразмеряване
  // менюто просто се затваря.
  function place() {
    if (!menu) return;
    const r = btn.getBoundingClientRect();
    const w = menu.offsetWidth || 210;
    const left = Math.max(8, Math.min(r.right - w, window.innerWidth - w - 8));
    menu.style.top = Math.round(r.bottom + 6) + 'px';
    menu.style.left = Math.round(left) + 'px';
    menu.style.maxHeight = Math.max(120, Math.round(window.innerHeight - r.bottom - 20)) + 'px';
  }

  function focusFirstItem() { const first = menu && menu.querySelector('button'); if (first) first.focus(); }

  function open(focusFirst) {
    if (menu) { if (focusFirst) focusFirstItem(); return; }
    menu = el('div', { class: 'menu actions-dropdown', role: 'menu' });
    list.forEach((it) => {
      menu.appendChild(el('button', {
        type: 'button', role: 'menuitem', text: it.label,
        onClick: () => { close(false); it.onClick(); },
      }));
    });
    document.body.appendChild(menu);
    menu.addEventListener('keydown', onMenuKeys);
    menu.addEventListener('focusout', onFocusOut);
    place();
    btn.setAttribute('aria-expanded', 'true');
    // Клик извън менюто го затваря (изчакваме текущия клик да отшуми).
    setTimeout(() => document.addEventListener('click', onDocClick), 0);
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('scroll', onViewportChange, true);
    if (focusFirst) focusFirstItem();
  }

  // Излезе ли фокусът извън бутона И менюто (напр. с Tab), менюто се затваря.
  // Отлага се, за да не изчезне ПРЕДИ клика върху негов елемент (Safari не
  // фокусира бутони при клик).
  function onFocusOut() {
    setTimeout(() => { if (menu && !inside(document.activeElement)) close(false); }, 0);
  }

  // Клавишите В менюто (то е в <body>, затова слушателят е върху него).
  function onMenuKeys(e) {
    if (e.key === 'Escape') { e.stopPropagation(); close(true); return; }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const opts = [...menu.querySelectorAll('button')];
    const i = opts.indexOf(document.activeElement);
    if (i < 0) return;
    e.preventDefault();
    const next = e.key === 'ArrowDown' ? (i + 1) % opts.length : (i - 1 + opts.length) % opts.length;
    opts[next].focus();
  }

  // БЕЗ stopPropagation: кликът трябва да стигне до document, за да се
  // затворят другите менюта (за фирма и за профил) в рамката.
  btn.addEventListener('click', () => { if (menu) close(false); else open(false); });
  btn.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); open(true); }       // отваря и влиза
    else if (e.key === 'Escape' && menu) { e.stopPropagation(); close(true); }
  });
  wrap.addEventListener('focusout', onFocusOut);

  wrap.appendChild(btn);
  return wrap;
}

// Номер на документ с ПРИГЛУШЕНИ водещи нули: „0000001156" се чете по
// последните цифри, а шестте нули само пречат. Пълният номер остава в
// текста (копиране/четец) — само цветът се сменя.
export function docNo(number) {
  const s = String(number || '');
  const m = s.match(/^(0+)(\d.*)$/);
  if (!m) return el('span', { class: 'doc-no', text: s });
  // (В.9) В списъците водещите нули са шум — показва се краткият номер,
  // пълният стои в подсказката. В PDF и на печат номерът остава пълен
  // (там е законово нужен) — те не минават оттук.
  return el('span', { class: 'doc-no', title: '№ ' + s, text: m[2] });
}

// opts.warn — предупредителен кант и лек червен фон (просрочени задължения);
// opts.subAlarm — подтекстът е тревога (червен, удебелен), а не бележка.
export function statCard(label, value, sub, opts) {
  const o = opts || {};
  return el('div', { class: 'stat' + (o.warn ? ' warn' : '') },
    el('div', { class: 'label', text: label }),
    el('div', { class: 'value', text: value }),
    sub ? el('div', { class: 'sub' + (o.subAlarm ? ' alarm' : ''), text: sub }) : null);
}

// Приглушаване на преобладаващия статус в списъци с документи.
// При 300 реда „Чернова" колоната „Статус" не казва нищо, но пълноцветен
// етикет на всеки ред крещи напразно. Затова: най-честият статус се показва
// като приглушен текст, а различаващите се получават цветно етикетче — окото
// хваща изключенията. Колоната ОСТАВА видима дори когато статусът е един:
// липсваща колона обърква („счупено ли е?"), а „всички чернови" и „всички
// платени" трябва да изглеждат различно.
// fallback: празен/липсващ статус се брои и показва като този статус (при
// документите — „draft"), а не като отделна стойност „undefined".
// Просрочена фактура = НЕплатена с минал падеж (без падеж важи датата на
// издаване). ЕДИНСТВЕНОТО правило — ползват го и Таблото, и списъкът на
// фактурите (находка №48: Таблото броеше просрочени, а списъкът ги
// показваше в спокойно синьо „Изпратена"). today идва отвън — МЕСТНАТА
// дата на викащия (toISOString дава UTC, капанът от календара).
export function invoiceOverdue(inv, today) {
  // Черновата не е просрочена — още не е изпратена на клиента. Така я брои
  // и Таблото от 12.10.2026; списъкът и календарът го следват (грешка от
  // картата, 16.10.2026: на трите места се броеше различно).
  if (!inv || inv.status === 'paid' || inv.status === 'draft') return false;
  const due = String(inv.due_date || inv.issue_date || '').slice(0, 10);
  return !!due && due < today;
}

export function dominantStatus(items, fallback = 'draft') {
  const cnt = {};
  (items || []).forEach((i) => {
    const s = (i && i.status) || fallback;
    cnt[s] = (cnt[s] || 0) + 1;
  });
  return Object.keys(cnt).sort((a, b) => cnt[b] - cnt[a])[0] || fallback;
}

// Клетка за колоната „Статус": приглушен текст за преобладаващия, иначе цветен
// етикет. statusMap: { ключ: [надпис, цвят] }. Липсващ статус = fallback.
// Колоната „Статус" на фактури и проформи (Макс, 02.11.2026): изпратената
// пише кога е изпратена; отвори ли клиентът линка в писмото — „Получена" и
// кога (както в „Изпратени"); платената/фактурираната — само етикетът.
// doc.mail идва от сървъра (mailDates в routes/invoices.js и proformas.js).
function dtText(s) { const d = new Date(s); return dfmt(s) + (isNaN(d) ? '' : ' ' + String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0')); }
export function sentStatusCell(doc, dominant, statusMap) {
  const m = doc.mail || {};
  if (doc.status !== 'sent' || (!m.sent_at && !m.opened_at)) return statusCell(doc.status, dominant, statusMap);
  if (m.opened_at) {
    return el('td', {}, el('span', { class: 'tag teal', text: 'Получена', title: 'Клиентът отвори линка в писмото на ' + dtText(m.opened_at) }),
      el('div', { class: 'muted', style: 'font-size:11px', text: dtText(m.opened_at) }));
  }
  return el('td', {}, el('span', { class: 'tag blue', text: 'Изпратена', title: 'Изпратена по имейл на ' + dtText(m.sent_at) + '; клиентът още не е отворил линка' }),
    el('div', { class: 'muted', style: 'font-size:11px', text: dtText(m.sent_at) }));
}
// Филтърът „Получена": изпратена, чийто линк клиентът е отворил.
export const RECEIVED_OPTION = ['received', 'Получена (клиентът я е отворил)'];
export function matchesStatus(doc, wanted) {
  return wanted === 'received' ? (doc.status === 'sent' && !!(doc.mail && doc.mail.opened_at)) : doc.status === wanted;
}

export function statusCell(status, dominant, statusMap, fallback = 'draft') {
  const key = status || fallback;
  const st = (statusMap && statusMap[key]) || ['—', 'gray'];
  const span = key === dominant
    ? el('span', { style: 'color:var(--muted);font-size:12px', text: st[0] })
    : el('span', { class: 'tag ' + st[1], text: st[0] });
  return el('td', {}, span);
}
